import { useEffect, useRef, useState } from 'react';
import type { MediaItem, NarrationStatus, Story, StoryMood } from '../types';
import { buildShots } from '../player/shots';
import { DEFAULT_PREFERENCES } from '../player/types';
import { FILM_SERIF } from '../player/components/TitleCard';

/**
 * The checks that must pass before the film is played with narration, on top
 * of the server's own (narrationStatus.problems):
 *   * every line has its audio in English and in Urdu, with a known length;
 *   * every memory's hold in the real edit plan (buildShots) is at least as
 *     long as its narration, in both languages, and so is the opening card.
 */
export function readyProblems(story: Story, status: NarrationStatus, items: MediaItem[]): string[] {
  const problems = [...status.problems];
  const find = (lang: 'en' | 'ur', part: 'opening' | 'closing' | 'line', mediaId?: string) =>
    status.clips.find((c) => c.lang === lang && c.part === part && c.mediaId === mediaId && c.voice === status.voices[lang]);
  for (const lang of ['en', 'ur'] as const) {
    const name = lang === 'en' ? 'English' : 'Urdu';
    const parts: Array<{ part: 'opening' | 'closing' | 'line'; mediaId?: string; label: string }> = [
      { part: 'opening', label: 'opening' },
      ...story.lines.map((l, i) => ({ part: 'line' as const, mediaId: l.mediaId, label: `line ${i + 1}` })),
      { part: 'closing', label: 'closing' },
    ];
    const missing = parts.filter((p) => {
      const c = find(lang, p.part, p.mediaId);
      return !c || c.status !== 'ready' || c.durationMs <= 0;
    });
    if (missing.length > 0 && status.problems.length === 0) {
      problems.push(`${name} audio is missing for: ${missing.map((m) => m.label).join(', ')}.`);
    }
    // Holds from the same edit plan the player uses.
    const lines = new Map(story.lines.map((l) => [l.mediaId, { audioMs: find(lang, 'line', l.mediaId)?.durationMs ?? 0, text: l[lang] }]));
    const shots = buildShots(items, { prefs: DEFAULT_PREFERENCES, reducedMotion: false, photoMotion: 'motion', lines });
    // The voice starts after the lead-in and must end before the next memory comes in.
    const short = story.lines.filter((l) => {
      const shot = shots.find((s) => s.item.id === l.mediaId);
      const audio = find(lang, 'line', l.mediaId)?.durationMs ?? 0;
      return shot && shot.holdMs < shot.voiceDelayMs + audio;
    });
    if (short.length > 0) problems.push(`${name}: ${short.length} memories would end before their line is spoken.`);
  }
  return [...new Set(problems)];
}

export type PrepStage = 'writing' | 'audio' | 'checking' | 'ready' | 'failed';

/** Lines that change while it works: cheeky for light moods, gentle for tender ones. */
const QUIPS: Record<'light' | 'gentle', Record<'writing' | 'audio' | 'checking' | 'ready', string[]>> = {
  light: {
    writing: [
      'Asking the photos for the inside gossip…',
      'Counting who blinked in every picture…',
      'Writing jokes. Deleting the ones Ammi wouldn’t like…',
      'Finding the hero of this story (spoiler: everyone)…',
      'Translating the sarcasm into Roman Urdu, yaar…',
      'Pretending we were there the whole time…',
    ],
    audio: [
      'Warming up the narrator’s voice… mi mi mi…',
      'Take 2. Take 1 was way too dramatic…',
      'Teaching the narrator to say “yaar” properly…',
      'Adding dramatic pauses for the punchlines…',
      'The narrator wants a chai break. Denied…',
    ],
    checking: ['Making sure nobody gets cut off mid-joke…', 'Timing every punchline to its photo…'],
    ready: ['Popcorn ready? The film is.', 'Ready. Try not to laugh at your own photos.'],
  },
  gentle: {
    writing: ['Looking through the photos…', 'Finding the moments that matter…', 'Writing it the way you’d remember it…'],
    audio: ['Recording the narration…', 'Letting each line breathe…'],
    checking: ['Making sure every line fits its memory…'],
    ready: ['Your film is ready.'],
  },
};
const GENTLE_MOODS = new Set<StoryMood>(['EMOTIONAL', 'NOSTALGIC', 'CALM']);

function StepIcon({ state }: { state: 'pending' | 'active' | 'done' | 'failed' }) {
  if (state === 'done') {
    return (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-400/90 text-black" aria-label="done">
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
          <path d="M5 12l5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (state === 'failed') {
    return (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-red-400/90 text-xs font-bold text-black" aria-label="failed">
        !
      </span>
    );
  }
  if (state === 'active') {
    return <span className="h-6 w-6 shrink-0 animate-spin rounded-full border-2 border-white/20 border-t-white" aria-label="working" />;
  }
  return <span className="h-6 w-6 shrink-0 rounded-full border-2 border-white/15" aria-label="waiting" />;
}

/**
 * "Getting things ready": shown over the episode page after "Make it a story"
 * while the story is written, both narrations are made and the checks run.
 * Behind it, the film's opening screen (title over the cover), so it is
 * clear what is being made. Play is enabled only when every check passes.
 */
export function StoryPrepOverlay({
  mood,
  title,
  backdropUrl,
  dateLine,
  stage,
  status,
  problems,
  error,
  onPlay,
  onRetry,
  onClose,
}: {
  /** The story's mood: decides whether the waiting lines are cheeky or gentle. */
  mood?: StoryMood | null;
  title: string;
  backdropUrl: string | null;
  dateLine: string | null;
  stage: PrepStage;
  status: NarrationStatus | null;
  problems: string[];
  error: string | null;
  onPlay: () => void;
  onRetry: () => void;
  onClose: () => void;
}) {
  const [started] = useState(() => Date.now());
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (stage === 'ready' || stage === 'failed') return;
    const t = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(t);
  }, [stage]);
  const card = useRef<HTMLDivElement>(null);
  useEffect(() => {
    card.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus();
  }, [stage]);

  const count = (lang: 'en' | 'ur') => {
    const list = (status?.clips ?? []).filter((c) => c.lang === lang);
    return { ready: list.filter((c) => c.status === 'ready').length, total: list.length, failed: list.some((c) => c.status === 'error') };
  };
  const en = count('en');
  const ur = count('ur');
  const narration = { ready: en.ready + ur.ready, total: en.total + ur.total, failed: en.failed || ur.failed };
  const quips = QUIPS[mood && GENTLE_MOODS.has(mood) ? 'gentle' : 'light'];
  const quipList = stage === 'failed' ? null : quips[stage];
  // A new line every 4 seconds, in a stable order per stage.
  const quip = quipList ? quipList[Math.floor(tick / 4) % quipList.length] : null;
  const order: PrepStage[] = ['writing', 'audio', 'checking', 'ready'];
  const at = stage === 'failed' ? -1 : order.indexOf(stage);
  const stepState = (i: number, failed = false): 'pending' | 'active' | 'done' | 'failed' =>
    stage === 'failed' && failed ? 'failed' : at > i || stage === 'ready' ? 'done' : at === i ? 'active' : 'pending';
  const audioState = (c: { ready: number; total: number; failed: boolean }) =>
    c.total > 0 && c.ready === c.total ? 'done' : c.failed ? 'failed' : stage === 'audio' ? 'active' : at > 1 ? 'done' : 'pending';
  const writingFailed = stage === 'failed' && !status;
  const total = en.total + ur.total;
  const percent =
    stage === 'ready'
      ? 100
      : stage === 'writing'
        ? Math.min(30, Math.round((Date.now() - started) / 1000))
        : 30 + (total > 0 ? Math.round(((en.ready + ur.ready) / total) * 60) : 0) + (stage === 'checking' ? 8 : 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#0b0907] text-[#f3ece2]" role="dialog" aria-modal="true" aria-labelledby="prep-heading">
      {/* The film's opening screen, behind the card. */}
      {backdropUrl && <img src={backdropUrl} alt="" className="fixed inset-0 h-full w-full object-cover opacity-25 blur-sm" />}
      <div className="fixed inset-0 bg-gradient-to-b from-[#0b0907]/60 via-[#0b0907]/70 to-[#0b0907]" aria-hidden="true" />
      <div className="relative flex min-h-full flex-col items-center justify-center px-4 py-10">
        <div className="mb-8 text-center" aria-hidden="true">
          <h2
            className="mx-auto max-w-[20ch] text-[clamp(2rem,7vw,4rem)] leading-[1.05] [text-wrap:balance] text-[#f3ece2]"
            style={{ fontFamily: FILM_SERIF }}
          >
            {title}
          </h2>
          <span className="mx-auto mt-5 mb-4 block h-px w-14 bg-[#f3ece2]/40" />
          {dateLine && <p className="text-sm text-[#f3ece2]/60 tabular-nums">{dateLine}</p>}
        </div>

        <div
          ref={card}
          className="w-full max-w-md rounded-2xl bg-[#17140f]/90 p-5 shadow-2xl ring-1 ring-[#f3ece2]/10 backdrop-blur-xl sm:p-6"
          aria-live="polite"
        >
          <h3 id="prep-heading" className="text-lg font-semibold">
            {stage === 'ready' ? 'Ready to play' : stage === 'failed' ? 'Not ready yet' : 'Getting things ready'}
          </h3>
          {quip && (
            <p key={quip} className="film-fade mt-1 min-h-5 text-sm text-white/60 italic">
              {quip}
            </p>
          )}
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className={`h-full rounded-full transition-[width] duration-700 ${stage === 'failed' ? 'bg-red-400' : 'bg-[#f3ece2]'}`}
              style={{ width: `${percent}%` }}
            />
          </div>

          <ol className="mt-5 space-y-3 text-sm">
            <li className="flex items-center gap-3">
              <StepIcon state={stepState(0, writingFailed)} />
              <span className="flex-1">Writing the story</span>
              {stage === 'writing' && <span className="text-xs text-white/40 tabular-nums">{Math.round((Date.now() - started) / 1000)} s</span>}
            </li>
            <li className="flex items-center gap-3">
              <StepIcon state={audioState(narration)} />
              <span className="flex-1">Recording the narration</span>
              {narration.total > 0 && (
                <span className="text-xs text-white/50 tabular-nums">
                  {narration.ready}/{narration.total}
                </span>
              )}
            </li>
            <li className="flex items-center gap-3">
              <StepIcon state={stage === 'failed' && status ? 'failed' : stepState(2)} />
              <span className="flex-1">Checking every line fits its memory</span>
            </li>
          </ol>

          {stage === 'failed' && (
            <div className="mt-5 rounded-lg bg-red-500/10 p-3 text-sm text-red-100 ring-1 ring-red-400/20" role="alert">
              {error && <p>{error}</p>}
              {problems.length > 0 && (
                <ul className="list-disc space-y-1 pl-4">
                  {problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={onPlay}
              disabled={stage !== 'ready'}
              data-autofocus={stage === 'ready' ? '' : undefined}
              className="focus-ring flex min-h-11 items-center gap-2 rounded-md bg-white px-6 py-2.5 text-sm font-bold text-black transition hover:bg-white/85 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
              Play
            </button>
            {stage === 'failed' && (
              <button
                onClick={onRetry}
                data-autofocus=""
                className="focus-ring min-h-11 rounded-md bg-white/15 px-5 py-2.5 text-sm font-semibold hover:bg-white/25"
              >
                Try again
              </button>
            )}
            <button onClick={onClose} className="focus-ring min-h-11 rounded-md px-4 py-2.5 text-sm text-white/60 hover:text-white">
              {stage === 'ready' || stage === 'failed' ? 'Close' : 'Hide'}
            </button>
          </div>
          {status && status.charactersSent > 0 && (
            <p className="mt-3 text-[11px] text-white/35">{status.charactersSent} characters of story text sent for narration.</p>
          )}
        </div>
      </div>
    </div>
  );
}
